/**
 * Article Type Form Component
 * DS-Wiki Knowledge Share Module
 * Includes custom fields management
 */

import { WIKI_POPUP_GRADIENT } from '@/modules/core/wiki/constants/popupHeader';
import { useState, useEffect } from 'react';
import dsWikiService from '../../services/dsWikiService';
import type { ArticleType, ArticleTypeInput, CustomFieldDefinition } from '../../services/types';
import { useToast } from '../../../../../hooks/useToast';
import { FormModal, FormInput, ChangeHistory } from '../../../../../components/base';

interface ArticleTypeFormProps {
  isOpen: boolean;
  articleType: ArticleType | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  artcl_type_nm: string;
  artcl_type_cd: string;
  dscn_tx: string;
  sqnce_nu: number;
  wiki_stle_inpt_in: number;
  nd_aprvl_in: number;
  nd_crtn_in: number;
  a_in: boolean;
}

interface CustomFieldFormData extends CustomFieldDefinition {
  isNew?: boolean;
  isModified?: boolean;
  isDeleted?: boolean;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: 'ri-text' },
  { value: 'textarea', label: 'Text Area', icon: 'ri-file-text-line' },
  { value: 'number', label: 'Number', icon: 'ri-hashtag' },
  { value: 'date', label: 'Date', icon: 'ri-calendar-line' },
  { value: 'select', label: 'Dropdown', icon: 'ri-arrow-down-s-line' },
  { value: 'checkbox', label: 'Checkbox', icon: 'ri-checkbox-line' },
];

const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const createEmptyCustomField = (): CustomFieldFormData => ({
  field_id: generateFieldId(),
  field_name: '',
  field_type: 'text',
  is_required: false,
  placeholder: '',
  options: [],
  isNew: true,
  isModified: false,
  isDeleted: false,
});

export default function ArticleTypeForm({ isOpen, articleType, onClose, onSuccess }: ArticleTypeFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'fields'>('details');
  const { success } = useToast();
  const [formData, setFormData] = useState<FormData>({
    artcl_type_nm: '',
    artcl_type_cd: '',
    dscn_tx: '',
    sqnce_nu: 0,
    wiki_stle_inpt_in: 0,
    nd_aprvl_in: 0,
    nd_crtn_in: 0,
    a_in: true,
  });
  const [customFields, setCustomFields] = useState<CustomFieldFormData[]>([]);
  const [expandedFieldIndex, setExpandedFieldIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (articleType) {
      setFormData({
        artcl_type_nm: articleType.artcl_type_nm || '',
        artcl_type_cd: articleType.artcl_type_cd || '',
        dscn_tx: articleType.dscn_tx || '',
        sqnce_nu: articleType.sqnce_nu || 0,
        wiki_stle_inpt_in: articleType.wiki_stle_inpt_in || 0,
        nd_aprvl_in: articleType.nd_aprvl_in || 0,
        nd_crtn_in: articleType.nd_crtn_in || 0,
        a_in: articleType.a_in !== false,
      });
      // Load existing custom fields
      const existingFields = articleType.cstm_flds_jn || [];
      setCustomFields(existingFields.map(f => ({
        ...f,
        isNew: false,
        isModified: false,
        isDeleted: false,
      })));
    } else {
      setFormData({
        artcl_type_nm: '',
        artcl_type_cd: '',
        dscn_tx: '',
        sqnce_nu: 0,
        wiki_stle_inpt_in: 0,
        nd_aprvl_in: 0,
        nd_crtn_in: 0,
        a_in: true,
      });
      setCustomFields([]);
    }
    setErrors({});
    setApiError(null);
    setActiveTab('details');
    setExpandedFieldIndex(null);
  }, [articleType, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof FormData]) setErrors(prev => ({ ...prev, [name]: undefined }));
    if (apiError) setApiError(null);
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.artcl_type_nm.trim()) newErrors.artcl_type_nm = 'Article type name is required';
    else if (formData.artcl_type_nm.length > 100) newErrors.artcl_type_nm = 'Name must be less than 100 characters';
    if (!formData.artcl_type_cd.trim()) newErrors.artcl_type_cd = 'Code is required';
    else if (formData.artcl_type_cd.length > 20) newErrors.artcl_type_cd = 'Code must be less than 20 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setActiveTab('details');
      return;
    }

    setIsSubmitting(true);
    try {
      // Filter out deleted fields and prepare custom fields for saving
      const fieldsToSave: CustomFieldDefinition[] = customFields
        .filter(f => !f.isDeleted)
        .map(({ isNew, isModified, isDeleted, ...field }) => field);

      const dataToSave: ArticleTypeInput = {
        artcl_type_nm: formData.artcl_type_nm,
        artcl_type_cd: formData.artcl_type_cd,
        dscn_tx: formData.dscn_tx || undefined,
        sqnce_nu: formData.sqnce_nu,
        wiki_stle_inpt_in: formData.wiki_stle_inpt_in,
        nd_aprvl_in: formData.nd_aprvl_in,
        nd_crtn_in: formData.nd_crtn_in,
        cstm_flds_jn: fieldsToSave.length > 0 ? fieldsToSave : undefined,
        a_in: formData.a_in,
      };
      if (articleType?.dswiki_artcl_type_id) dataToSave.dswiki_artcl_type_id = articleType.dswiki_artcl_type_id;
      await dsWikiService.saveArticleType(dataToSave);
      success(articleType ? 'Article type updated successfully!' : 'Article type created successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving article type:', error);
      setApiError(error?.message || 'Failed to save article type');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom Fields Management
  const handleAddField = () => {
    const newField = createEmptyCustomField();
    setCustomFields(prev => [...prev, newField]);
    setExpandedFieldIndex(customFields.length);
  };

  const handleFieldChange = (index: number, field: keyof CustomFieldFormData, value: any) => {
    setCustomFields(prev => prev.map((f, i) => {
      if (i === index) {
        return { ...f, [field]: value, isModified: !f.isNew };
      }
      return f;
    }));
  };

  const handleDeleteField = (index: number) => {
    setCustomFields(prev => prev.map((f, i) => {
      if (i === index) {
        if (f.isNew) {
          return null as any;
        }
        return { ...f, isDeleted: true };
      }
      return f;
    }).filter(Boolean));
    setExpandedFieldIndex(null);
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= customFields.length) return;

    setCustomFields(prev => {
      const newFields = [...prev];
      const temp = newFields[index];
      newFields[index] = newFields[newIndex];
      newFields[newIndex] = temp;
      return newFields.map((f) => ({
        ...f,
        isModified: f.isModified || !f.isNew,
      }));
    });
    setExpandedFieldIndex(newIndex);
  };

  const handleOptionChange = (fieldIndex: number, optionIndex: number, newValue: string) => {
    setCustomFields(prev => prev.map((f, i) => {
      if (i === fieldIndex) {
        const newOptions = [...(f.options || [])];
        newOptions[optionIndex] = newValue;
        return { ...f, options: newOptions, isModified: !f.isNew };
      }
      return f;
    }));
  };

  const handleAddOption = (fieldIndex: number) => {
    setCustomFields(prev => prev.map((f, i) => {
      if (i === fieldIndex) {
        return { ...f, options: [...(f.options || []), ''], isModified: !f.isNew };
      }
      return f;
    }));
  };

  const handleRemoveOption = (fieldIndex: number, optionIndex: number) => {
    setCustomFields(prev => prev.map((f, i) => {
      if (i === fieldIndex) {
        const newOptions = (f.options || []).filter((_, oi) => oi !== optionIndex);
        return { ...f, options: newOptions, isModified: !f.isNew };
      }
      return f;
    }));
  };

  const visibleFields = customFields.filter(f => !f.isDeleted);

  return (
    <FormModal
      popup={{ gradient: WIKI_POPUP_GRADIENT, icon: 'ri-article-line' }}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={articleType ? 'Edit Article Type' : 'Add New Article Type'}
      submitText={articleType ? 'Update' : 'Create'}
      submitColor="blue"
      isSubmitting={isSubmitting}
      size="lg"
    >
      {apiError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
          <div className="text-sm text-red-700">{apiError}</div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <i className="ri-information-line mr-1.5"></i>
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('fields')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'fields'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <i className="ri-list-settings-line mr-1.5"></i>
          Custom Fields
          {visibleFields.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
              {visibleFields.length}
            </span>
          )}
        </button>
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <div className="space-y-4">
          <FormInput label="Article Type Name" name="artcl_type_nm" value={formData.artcl_type_nm} onChange={handleInputChange} placeholder="e.g., Government Order, Circular" required error={errors.artcl_type_nm} />
          <FormInput label="Code" name="artcl_type_cd" value={formData.artcl_type_cd} onChange={handleInputChange} placeholder="e.g., GO, CIR" required helperText="Unique code for the article type" error={errors.artcl_type_cd} disabled={!!articleType} />
          <FormInput label="Description" name="dscn_tx" value={formData.dscn_tx} onChange={handleInputChange} placeholder="Brief description of the article type" />
          <FormInput label="Sequence" name="sqnce_nu" type="number" value={formData.sqnce_nu.toString()} onChange={handleInputChange} placeholder="Display order" />

          <div className="grid grid-cols-2 gap-4 mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.nd_aprvl_in === 1}
                onChange={(e) => handleCheckboxChange('nd_aprvl_in', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Needs Approval</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.nd_crtn_in === 1}
                onChange={(e) => handleCheckboxChange('nd_crtn_in', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Needs Curation</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.wiki_stle_inpt_in === 1}
                onChange={(e) => handleCheckboxChange('wiki_stle_inpt_in', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Wiki Style Input</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.a_in}
                onChange={(e) => setFormData(prev => ({ ...prev, a_in: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          {articleType && <ChangeHistory lastChange={(articleType as any).lastChange} />}
        </div>
      )}

      {/* Custom Fields Tab */}
      {activeTab === 'fields' && (
        <div className="space-y-4">
          {/* Add Field Button */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Define custom fields that will appear when creating articles of this type.
            </p>
            <button
              type="button"
              onClick={handleAddField}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i className="ri-add-line"></i>
              Add Field
            </button>
          </div>

          {/* Fields List */}
          {visibleFields.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <i className="ri-list-settings-line text-4xl text-gray-300 mb-2"></i>
              <p className="text-gray-500">No custom fields defined yet.</p>
              <p className="text-gray-400 text-sm">Click "Add Field" to create your first custom field.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {visibleFields.map((field, index) => {
                const actualIndex = customFields.findIndex(f => f.field_id === field.field_id);
                const isExpanded = expandedFieldIndex === actualIndex;
                const fieldType = FIELD_TYPES.find(t => t.value === field.field_type);
                const showOptions = field.field_type === 'select';

                return (
                  <div
                    key={field.field_id}
                    className={`border rounded-lg transition-all ${
                      isExpanded ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Field Header */}
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      onClick={() => setExpandedFieldIndex(isExpanded ? null : actualIndex)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleMoveField(actualIndex, 'up'); }}
                            disabled={index === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-up-s-line text-sm"></i>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleMoveField(actualIndex, 'down'); }}
                            disabled={index === visibleFields.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-down-s-line text-sm"></i>
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className={`${fieldType?.icon || 'ri-text'} text-blue-500`}></i>
                          <div>
                            <div className="font-medium text-gray-900">
                              {field.field_name || 'New Field'}
                              {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <span className="bg-gray-100 px-1.5 py-0.5 rounded">{field.field_id}</span>
                              <span>{fieldType?.label || 'Text'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {field.isNew && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">New</span>
                        )}
                        {field.isModified && !field.isNew && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Modified</span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDeleteField(actualIndex); }}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-gray-400`}></i>
                      </div>
                    </div>

                    {/* Field Details (Expanded) */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 p-4 space-y-4 bg-white rounded-b-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Field Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={field.field_name}
                              onChange={(e) => handleFieldChange(actualIndex, 'field_name', e.target.value)}
                              placeholder="e.g., Reference Number"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                            <select
                              value={field.field_type}
                              onChange={(e) => handleFieldChange(actualIndex, 'field_type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                              {FIELD_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => handleFieldChange(actualIndex, 'placeholder', e.target.value)}
                            placeholder="Placeholder text shown in the input"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        {/* Options for SELECT */}
                        {showOptions && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Dropdown Options</label>
                            <div className="space-y-2">
                              {(field.options || []).map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) => handleOptionChange(actualIndex, optIndex, e.target.value)}
                                    placeholder={`Option ${optIndex + 1}`}
                                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveOption(actualIndex, optIndex)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => handleAddOption(actualIndex)}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <i className="ri-add-line"></i> Add Option
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-6 pt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.is_required}
                              onChange={(e) => handleFieldChange(actualIndex, 'is_required', e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Required Field</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
}
